import { supabase } from '../lib/supabaseClient';

export const propertyService = {
    /**
     * Search properties with various filters
     * Mirrors SupabasePropertyRepository.dart logic
     */
    async searchProperties({
        query,
        locationText,
        propertyTypes = [],
        bhkOptions = [],
        minPrice,
        maxPrice,
        furnishingOptions = [],
        localities = [],
        sortBy,
        listingType,
        limit = 10,
        offset = 0
    }) {
        try {
            const hasLocalities = localities && localities.length > 0;
            const hasLocationText = locationText && locationText.length > 0;

            // Start building query
            let queryBuilder = supabase
                .from('property')
                .select(
                    (hasLocalities || hasLocationText)
                        ? '*, areas!inner(*)'
                        : '*, areas(*)',
                    { count: 'exact' }
                );

            // Status filter (active or rented)
            queryBuilder = queryBuilder.in('status', ['active', 'rented']);

            // Location Text Search (City or Locality)
            if (hasLocationText) {
                // ILIKE on joined table columns. 
                // Note: supabase-js v2 syntax for foreign table filter usually requires special handling if OR is involved across columns.
                // However, the syntax `locality.ilike.%term%,city.ilike.%term%` works with PostgREST if the relationship is detected.
                // We use the foreign table hint options.
                queryBuilder = queryBuilder.or(`name.ilike.%${locationText}%,city.ilike.%${locationText}%`, { foreignTable: 'areas' });
            }

            // Localities filter (List)
            if (hasLocalities) {
                queryBuilder = queryBuilder.in('areas.name', localities);
            }

            // Text Search (Title or Description)
            if (query && query.length > 0) {
                queryBuilder = queryBuilder.or(`title.ilike.%${query}%,description.ilike.%${query}%`);
            }

            // Listing Type (Rent/Lease)
            if (listingType) {
                queryBuilder = queryBuilder.ilike('listing_type', listingType);
            }

            // Property Type
            if (propertyTypes && propertyTypes.length > 0) {
                queryBuilder = queryBuilder.in('property_type', propertyTypes);
            }

            // BHK
            if (bhkOptions && bhkOptions.length > 0) {
                queryBuilder = queryBuilder.in('bhk', bhkOptions);
            }

            // Price Range
            if (minPrice !== undefined && minPrice !== null && minPrice !== '') {
                queryBuilder = queryBuilder.gte('expected_rent', minPrice);
            }
            if (maxPrice !== undefined && maxPrice !== null && maxPrice !== '') {
                queryBuilder = queryBuilder.lte('expected_rent', maxPrice);
            }

            // Furnishing
            if (furnishingOptions && furnishingOptions.length > 0) {
                const capitalizedOptions = furnishingOptions.map(f => {
                    if (!f) return f;
                    if (f.toLowerCase() === 'semi') return 'Semi';
                    if (f.toLowerCase() === 'fully') return 'Fully';
                    if (f.toLowerCase() === 'unfurnished') return 'Unfurnished';
                    return f.charAt(0).toUpperCase() + f.slice(1).toLowerCase();
                });
                queryBuilder = queryBuilder.in('furnishing', capitalizedOptions);
            }

            // Sorting
            if (sortBy) {
                switch (sortBy) {
                    case 'newest':
                    case 'Newest First':
                        queryBuilder = queryBuilder.order('created_at', { ascending: false });
                        break;
                    case 'price_asc':
                    case 'Price: Low to High':
                        queryBuilder = queryBuilder.order('expected_rent', { ascending: true });
                        break;
                    case 'price_desc':
                    case 'Price: High to Low':
                        queryBuilder = queryBuilder.order('expected_rent', { ascending: false });
                        break;
                    default:
                        queryBuilder = queryBuilder.order('created_at', { ascending: false });
                        break;
                }
            } else {
                queryBuilder = queryBuilder.order('created_at', { ascending: false });
            }

            // Pagination
            queryBuilder = queryBuilder.range(offset, offset + limit - 1);

            const { data, count, error } = await queryBuilder;

            if (error) throw error;

            return {
                properties: (data || []).map(p => ({
                    id: p.id,
                    title: p.title || `${p.bhk} BHK ${p.property_type} for ${p.listing_type || 'Rent'} in ${p.areas?.name || 'City'}`,
                    price: p.expected_rent,
                    type: p.property_type,
                    bhk: p.bhk,
                    location: p.areas ? `${p.areas.name}, ${p.areas.city}` : 'Unknown',
                    image: (p.media_url && p.media_url.length > 0) ? p.media_url[0] : null,
                    furnishing: p.furnishing,
                    sqft: p.sqft,
                    deposit: p.security_deposit,
                    listingType: p.listing_type,
                    created_at: p.created_at
                })),
                totalCount: count || 0
            };

        } catch (error) {
            console.error('Error searching properties:', error);
            throw error;
        }
    },

    /**
     * Get unique localities that have active properties
     */
    async getUniqueLocalities() {
        try {
            const { data, error } = await supabase
                .from('property')
                .select('areas!inner(name)')
                .eq('status', 'active');

            if (error) throw error;

            if (!data || data.length === 0) return [];

            const localities = data
                .map(item => item.areas?.name)
                .filter(loc => loc && loc.trim() !== '');

            const uniqueLocalities = [...new Set(localities)].sort();
            return uniqueLocalities;

        } catch (error) {
            console.error('Error fetching unique localities:', error);
            return [];
        }
    },

    /**
     * Get exact count of active properties matching the selected localities.
     * Uses a HEAD request pattern to minimize data transfer.
     */
    async getPropertyCountForLocalities(localities) {
        if (!localities || localities.length === 0) return 0;

        try {
            const { count, error } = await supabase
                .from('property')
                .select('*, areas!inner(*)', { count: 'exact', head: true })
                .eq('status', 'active')
                .in('areas.name', localities);

            if (error) throw error;
            return count || 0;
        } catch (error) {
            console.error('Error getting property count:', error);
            return 0; // Soft fail, return 0 if error so the UI doesn't crash
        }
    }
};
